/**
 * Pure helpers for the Terraform plan-approval panel: generic over the plan / detail record.
 */
import type { DeploymentStatus, PlanStatus, TerraformPlanRecord } from '@/types'

export const PLAN_STATUS_LABELS: Record<PlanStatus, string> = {
  planned: 'Awaiting approval',
  approved: 'Approved',
  rejected: 'Rejected',
  applying: 'Applying',
  applied: 'Applied',
  failed: 'Failed',
}

export type PlanTone = 'muted' | 'running' | 'success' | 'error' | 'warning'

export const PLAN_STATUS_TONE: Record<PlanStatus, PlanTone> = {
  planned: 'warning',
  approved: 'running',
  applying: 'running',
  applied: 'success',
  rejected: 'muted',
  failed: 'error',
}

/** Plans arrive newest first; be defensive and sort by created_at anyway. */
export function latestPlan<P extends { created_at: string }>(plans: P[] | undefined): P | undefined {
  if (!plans?.length) return undefined
  return [...plans].sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))[0]
}

/** Approve / reject is offered only for the awaiting state and when the API allows this user. */
export function canDecidePlan(
  detail: { can_approve: boolean; plans: Array<{ created_at: string }>; config?: unknown } | undefined,
  status: DeploymentStatus | string,
): boolean {
  return !!detail?.can_approve && status === 'awaiting_plan_approval' && !!latestPlan(detail.plans)
}

export function formatPlanCounts(plan: Pick<TerraformPlanRecord, 'plan_summary'>): string {
  const s = plan.plan_summary
  if (!s) return 'No summary'
  return `+${s.add} to add · ~${s.change} to change · -${s.destroy} to destroy`
}
